import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { generateTCDSPdf } from "../utils/tcdsPdfGenerator";
import { generateAPI6DPdf } from "../utils/Generateapi6d";
import { generateTCDSExcel } from "../utils/generateTCDSExcel";
import { generateAPI6DExcel } from "../utils/generateAPI6DExcel";
import "./ManageTCDS.css";
import "./ViewTCDS.css";

function ViewTCDS() {
  const navigate = useNavigate();
  const [tcdsList, setTcdsList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTCDS, setSelectedTCDS] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Role check
  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const canEdit = userRole === "sales" || userRole === "admin";

  const normalizeTcdsType = (type) => {
    const normalized = String(type || "").trim().toLowerCase();
    if (normalized === "api 6d") return "API 6D";
    if (normalized === "api 600") return "API 600";
    return "Automation/Standard";
  };

  const getRouteForType = (type, id = null) => {
    const normalizedType = normalizeTcdsType(type);

    if (normalizedType === "API 6D") {
      return id ? `/tcds/api-6d/edit/${id}` : "/tcds/api-6d/new";
    }
    if (normalizedType === "API 600") {
      return id ? `/tcds/api-600/edit/${id}` : "/tcds/api-600/new";
    }
    return id ? `/tcds/edit/${id}` : "/tcds/new";
  };

  const getByIdEndpointForType = (type, id) => {
    const normalizedType = normalizeTcdsType(type);
    if (normalizedType === "API 6D") return `/tcds/api-6d/${id}`;
    if (normalizedType === "API 600") return `/tcds/api-600/${id}`;
    return `/tcds/${id}`;
  };

  const fetchAllTCDS = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all TCDS records without type filter
      const response = await axios.get("/tcds");
      const rows = response.data.data || [];
      setTcdsList(rows);
      setFilteredList(rows);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch TCDS records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTCDS();
  }, []);

  // Filter and search logic
  useEffect(() => {
    let result = [...tcdsList];

    // Filter by type
    if (filterType !== "all") {
      result = result.filter(
        (tcds) => normalizeTcdsType(tcds.tcds_type) === filterType
      );
    }

    // Filter by status
    if (filterStatus !== "all") {
      result = result.filter(
        (tcds) => (tcds.status || "draft").toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Search by term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((tcds) => {
        const sectionNumber = (tcds.section_number || tcds.tcds_no || "").toLowerCase();
        const customerName = (tcds.customer_name || tcds.customer || "").toLowerCase();
        const poNumber = (tcds.purchase_order_no || tcds.po_no || "").toLowerCase();
        const projectName = (tcds.project_name || "").toLowerCase();
        const tcdsType = (tcds.tcds_type || "").toLowerCase();

        return (
          sectionNumber.includes(term) ||
          customerName.includes(term) ||
          poNumber.includes(term) ||
          projectName.includes(term) ||
          tcdsType.includes(term)
        );
      });
    }

    setFilteredList(result);
  }, [tcdsList, searchTerm, filterType, filterStatus]);

  const getBadgeClass = (type) => {
    if (type === "API 6D") return "badge-api-6d";
    if (type === "API 600") return "badge-api-600";
    return "badge-automation";
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = (status || "draft").toLowerCase();
    if (statusLower === "approved") return "status-approved";
    if (statusLower === "pending") return "status-pending";
    if (statusLower === "rejected") return "status-rejected";
    return "status-draft";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleEditTCDS = (tcds) => {
    const route = getRouteForType(tcds.tcds_type, tcds.id);
    navigate(route);
  };

  const handleViewDetails = (tcds) => {
    setSelectedTCDS(tcds);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTCDS(null);
  };

  const handleDownloadPdf = async (tcds) => {
    try {
      setLoading(true);
      const recordType = tcds.tcds_type;
      const response = await axios.get(getByIdEndpointForType(recordType, tcds.id));
      
      const tcdsData = response.data.data || response.data;
      
      if (tcdsData) {
        try {
          if (normalizeTcdsType(recordType) === "API 6D") {
            generateAPI6DPdf(tcdsData);
          } else if (normalizeTcdsType(recordType) === "API 600") {
            const pdfResponse = await axios.post('/pdf/download-api600', tcdsData, {
              responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `TCDS_API600_${tcdsData.tcds_no || tcdsData.tcdsNo || 'export'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } else {
            generateTCDSPdf(tcdsData);
          }
        } catch (pdfError) {
          console.error("PDF Error:", pdfError);
          setError("Failed to generate PDF: " + pdfError.message);
        }
      } else {
        setError("Failed to fetch TCDS details for PDF generation");
      }
    } catch (error) {
      console.error("API Error:", error);
      setError(error.response?.data?.message || "Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async (tcds) => {
    try {
      setLoading(true);
      const recordType = tcds.tcds_type;
      const normalizedType = normalizeTcdsType(recordType);

      if (normalizedType === "Automation/Standard") {
        const tcdsNo = tcds.tcds_no;
        let sections = [];

        // ── Primary: use the /export endpoint which merges header + sections ──
        if (tcdsNo) {
          try {
            const exportResp = await axios.get(
              `/tcds/export/${encodeURIComponent(tcdsNo)}`
            );
            const exportData = exportResp.data.data || {};
            const tcdsDetails = exportData.tcdsDetails || {};
            const rawSections = exportData.sections || [];

            // Merge shared header fields into each section so Excel has full data
            sections = rawSections.map((sec) => ({
              ...tcdsDetails,   // shared header (customer, PO, dates, valve details, etc.)
              ...sec,           // section-specific data — overwrites header defaults
            }));
          } catch (exportErr) {
            console.warn("Export endpoint failed, falling back:", exportErr.message);
          }
        }

        // ── Fallback 1: /tcds/sections/:tcdsNo ──────────────────────────────
        if (sections.length === 0 && tcdsNo) {
          try {
            const sectionsResp = await axios.get(
              `/tcds/sections/${encodeURIComponent(tcdsNo)}`
            );
            sections = sectionsResp.data.data || [];
          } catch (_) { /* ignore */ }
        }

        // ── Fallback 2: single record by ID ─────────────────────────────────
        if (sections.length === 0) {
          const resp = await axios.get(getByIdEndpointForType(recordType, tcds.id));
          const single = resp.data.data || resp.data;
          if (single) sections = [single];
        }

        if (sections.length === 0) {
          setError("Failed to fetch TCDS details for Excel generation");
          return;
        }

        // Debug: log section data to verify valve details are present
        console.log("[ViewTCDS Excel] Sections to generate:", sections.length);
        sections.forEach((sec, i) => {
          console.log(`[ViewTCDS Excel] Section[${i}]: make=${sec.make || 'EMPTY'}, series=${sec.series || 'EMPTY'}, type=${sec.type || 'EMPTY'}, accessories_data=${!!sec.accessories_data}`);
        });

        await generateTCDSExcel(sections);
      } else if (normalizedType === "API 6D") {
        const response = await axios.get(getByIdEndpointForType(recordType, tcds.id));
        const tcdsData = response.data.data || response.data;
        if (tcdsData) {
          await generateAPI6DExcel(tcdsData);
        } else {
          setError("Failed to fetch TCDS details for Excel generation");
        }
      } else {
        setError("Excel download is not yet available for API 600 TCDS");
      }
    } catch (error) {
      console.error("API Error:", error);
      setError(error.response?.data?.message || "Failed to generate Excel");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterStatus("all");
  };

  return (
    <div className="tcds-container">
      <button className="tcds-back-button" onClick={() => navigate("/dashboard")}>
        ← Back to Dashboard
      </button>

      <div className="tcds-header">
        <h1 className="tcds-title">Search Records</h1>
        <p className="tcds-subtitle">
          Search and view all TCDS records across all types
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Section No, Customer, PO, Project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              ×
            </button>
          )}
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label>TCDS Type:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="Automation/Standard">Automation/Standard</option>
              <option value="API 6D">API 6D</option>
              <option value="API 600">API 600</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {(searchTerm || filterType !== "all" || filterStatus !== "all") && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>

        <div className="results-count">
          Showing {filteredList.length} of {tcdsList.length} records
        </div>
      </div>

      {/* Results Table */}
      <div className="tcds-table-container">
        {loading ? (
          <div className="loading-message">Loading TCDS records...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredList.length === 0 ? (
          <div className="empty-state">
            <p>No TCDS records found</p>
            {(searchTerm || filterType !== "all" || filterStatus !== "all") && (
              <button className="btn-clear-filters" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <table className="tcds-table">
            <thead>
              <tr>
                <th>TCDS No.</th>
                <th>Section No.</th>
                <th>Rev.</th>
                <th>Type</th>
                <th>Customer Name</th>
                <th>PO Number</th>
                <th>Project Name</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((tcds) => (
                <tr key={tcds.id}>
                  <td>{tcds.tcds_no || "N/A"}</td>
                  <td>{tcds.section_number || "N/A"}</td>
                  <td>{tcds.revision_no || 0}</td>
                  <td>
                    <span className={`badge ${getBadgeClass(tcds.tcds_type)}`}>
                      {tcds.tcds_type}
                    </span>
                  </td>
                  <td>{tcds.customer_name || tcds.customer || "N/A"}</td>
                  <td>{tcds.purchase_order_no || tcds.po_no || "N/A"}</td>
                  <td>{tcds.project_name || "N/A"}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(tcds.status)}`}>
                      {tcds.status || "Draft"}
                    </span>
                  </td>
                  <td>{formatDate(tcds.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-action btn-view"
                        onClick={() => handleViewDetails(tcds)}
                        title="View Details"
                      >
                        View
                      </button>
                      {(normalizeTcdsType(tcds.tcds_type) === "Automation/Standard" || normalizeTcdsType(tcds.tcds_type) === "API 6D") && (
                        <button
                          className="btn-action btn-download"
                          onClick={() => handleDownloadExcel(tcds)}
                          title="Download Excel"
                          style={{ background: '#d1fae5', color: '#059669' }}
                        >
                          Excel
                        </button>
                      )}
                      {canEdit && (
                        <button
                          className="btn-action btn-edit"
                          onClick={() => handleEditTCDS(tcds)}
                          title="Edit"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedTCDS && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>TCDS Details - {selectedTCDS.section_number || selectedTCDS.tcds_no} (Rev. {selectedTCDS.revision_no || 0})</h2>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="modal-section">
                <h3>Section Information</h3>
                <div className="modal-grid">
                  <div className="modal-field">
                    <label>TCDS No.:</label>
                    <span>{selectedTCDS.section_number || selectedTCDS.tcds_no || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>Rev. No.:</label>
                    <span>{selectedTCDS.revision_no || 0}</span>
                  </div>
                  <div className="modal-field">
                    <label>Section Title:</label>
                    <span>{selectedTCDS.section_title || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>TCDS Type:</label>
                    <span className={`badge ${getBadgeClass(selectedTCDS.tcds_type)}`}>
                      {selectedTCDS.tcds_type}
                    </span>
                  </div>
                  <div className="modal-field">
                    <label>Status:</label>
                    <span className={`status-badge ${getStatusBadgeClass(selectedTCDS.status)}`}>
                      {selectedTCDS.status || "Draft"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3>Customer Information</h3>
                <div className="modal-grid">
                  <div className="modal-field">
                    <label>Customer Name:</label>
                    <span>{selectedTCDS.customer_name || selectedTCDS.customer || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>Purchase Order No:</label>
                    <span>{selectedTCDS.purchase_order_no || selectedTCDS.po_no || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>PO Date:</label>
                    <span>{formatDate(selectedTCDS.po_date)}</span>
                  </div>
                  <div className="modal-field">
                    <label>Project Name:</label>
                    <span>{selectedTCDS.project_name || "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3>Additional Information</h3>
                <div className="modal-grid">
                  <div className="modal-field">
                    <label>Offer No:</label>
                    <span>{selectedTCDS.offer_no || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>Offer Date:</label>
                    <span>{formatDate(selectedTCDS.offer_date)}</span>
                  </div>
                  <div className="modal-field">
                    <label>TCDS Release Date:</label>
                    <span>{formatDate(selectedTCDS.tcds_release_date)}</span>
                  </div>
                  <div className="modal-field">
                    <label>CRM Date:</label>
                    <span>{formatDate(selectedTCDS.crm_date)}</span>
                  </div>
                  <div className="modal-field">
                    <label>Created Date:</label>
                    <span>{formatDate(selectedTCDS.created_at)}</span>
                  </div>
                  <div className="modal-field">
                    <label>Last Updated:</label>
                    <span>{formatDate(selectedTCDS.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewTCDS;
